// src/features/lockers/components/LockerActions.tsx
import React, { useState } from 'react';
import {
  usePermissions,
  useNotify,
  useRefresh,
  useDataProvider,
  useTranslate,
  useRedirect,
} from 'react-admin';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import {
  MoreVert,
  Edit,
  Delete,
  Block as BlockIcon,
  CheckCircle as UnblockIcon,
} from '@mui/icons-material';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';
import { Locker } from '../types';

interface LockerActionsProps {
  record: Locker;
}

const LockerActions: React.FC<LockerActionsProps> = ({ record }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const redirect = useRedirect();
  
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };

  // Edit action
  const handleEdit = () => {
    handleClose();
    redirect('edit', 'lockers', record.id);
  };

  // Open delete dialog
  const handleDeleteClick = () => {
    handleClose();
    setDeleteDialogOpen(true);
  };

  // Close delete dialog
  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    refresh();
  };

  // Open block dialog
  const handleBlockClick = () => {
    handleClose();
    setBlockReason('');
    setBlockDialogOpen(true);
  };

  const handleUnblock = async () => {
  handleClose();
  setIsProcessing(true);
  
  try {
    await dataProvider.unblockLocker(record.id);

    notify('resources.lockers.messages.unblock_success', { 
      type: 'success',
      messageArgs: { displayNumber: record.displayNumber }
    });
    
    refresh();
  } catch (error: any) {
    notify(error.message || 'resources.lockers.messages.unblock_error', { type: 'error' });
  } finally {
    setIsProcessing(false);
  }
};

  // Close block dialog
  const handleBlockDialogClose = () => {
    setBlockDialogOpen(false);
    setBlockReason('');
  };

  // Block locker
  const handleBlockConfirm = async () => {
  if (!blockReason.trim()) {
    notify('resources.lockers.messages.block_reason_required', { type: 'warning' });
    return;
  }

  setIsProcessing(true);
  try {
    console.log(record);
    await dataProvider.blockLocker(record.id, blockReason.trim());

    notify('resources.lockers.messages.block_success', { 
      type: 'success',
      messageArgs: { displayNumber: record.displayNumber }
    });
    
    handleBlockDialogClose();
    refresh();
  } catch (error: any) {
    notify(error.message || 'resources.lockers.messages.block_error', { type: 'error' });
  } finally {
    setIsProcessing(false);
  }
};

  // Determinar qué acciones mostrar según el estado
const statusLower = record.status?.toLowerCase() || '';
const canBlock = ['available', 'occupied', 'reserved'].includes(statusLower);
const canUnblock = statusLower === 'blocked';


  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={translate('ra.action.menu', 'Más opciones')}>
          <IconButton
            size={'small'}
            onClick={handleMenuOpen}
            sx={{ color: 'action.active' }}
          >
            <MoreVert fontSize={'small'} />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Menu
        id="locker-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{
          'aria-labelledby': 'locker-actions-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Edit */}
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('ra.action.edit')}</ListItemText>
        </MenuItem>
        
        {/* Block */}
        {canBlock && (
          <MenuItem onClick={handleBlockClick}>
            <ListItemIcon>
              <BlockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('resources.lockers.actions.block')}</ListItemText>
          </MenuItem>
        )}
        
        {/* Unblock */}
        {canUnblock && (
          <MenuItem onClick={handleUnblock} disabled={isProcessing}>
            <ListItemIcon>
              <UnblockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{translate('resources.lockers.actions.unblock')}</ListItemText>
          </MenuItem>
        )}
        
        {/* Delete */}
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>{translate('ra.action.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Block Dialog */}
      <Dialog 
        open={blockDialogOpen} 
        onClose={handleBlockDialogClose}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          {translate('resources.lockers.dialogs.block_title', {
            displayNumber: record.displayNumber
          })}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={translate('resources.lockers.fields.blockReason')}
            placeholder={translate('resources.lockers.placeholders.block_reason')}
            fullWidth
            multiline
            rows={2}
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            required
            helperText={translate('resources.lockers.helpers.block_reason')}
            sx={{mt:2}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBlockDialogClose} disabled={isProcessing}>
            {translate('ra.action.cancel')}
          </Button>
          <Button 
            onClick={handleBlockConfirm} 
            variant="contained" 
            color="warning"
            disabled={isProcessing || !blockReason.trim()}
          >
            {translate('resources.lockers.actions.block')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <BaseDeleteDialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        resource="lockers"
        recordId={record.id}
        title={translate('resources.lockers.dialogs.delete_title')}
        message={translate('resources.lockers.dialogs.delete_message', { 
          displayNumber: record.displayNumber 
        })}
        warningMessage={
          record.status === 'Occupied' 
            ? translate('resources.lockers.dialogs.delete_occupied_warning')
            : translate('resources.lockers.dialogs.delete_warning')
        }
      />
    </>
  );
};

export default LockerActions;